import IStorage from './IStorage';

/**
 * IndexedDB Storage Implementation
 * Provides async storage using browser's IndexedDB API
 */
class IndexedDBStorage extends IStorage {
    constructor(dbName = 'picourt-app-db', version = 1, storeName = 'key-value-store') {
        super();
        this.dbName = dbName;
        this.version = version;
        this.storeName = storeName;
        this.db = null;
        this.initPromise = this.init();
    }

    /**
     * Initialize IndexedDB connection
     * @private
     */
    async init() {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbName, this.version);

            request.onerror = () => {
                console.error('IndexedDB failed to open:', request.error);
                reject(request.error);
            };

            request.onsuccess = () => {
                this.db = request.result;
                console.log('IndexedDB opened successfully');
                resolve(this.db);
            };

            request.onupgradeneeded = (event) => {
                const db = event.target.result;

                // Create object store if it doesn't exist
                if (!db.objectStoreNames.contains(this.storeName)) {
                    db.createObjectStore(this.storeName);
                }
            };
        });
    }

    /**
     * Ensure database is initialized before operations
     * @private
     */
    async ensureDB() {
        if (!this.db) {
            await this.initPromise;
        }
        return this.db;
    }

    /**
     * Get a value from IndexedDB
     * @param {string} key
     * @returns {Promise<any>}
     */
    async get(key) {
        const db = await this.ensureDB();

        return new Promise((resolve, reject) => {
            try {
                const transaction = db.transaction([this.storeName], 'readonly');
                const store = transaction.objectStore(this.storeName);
                const request = store.get(key);

                request.onsuccess = () => {
                    const result = request.result !== undefined ? request.result : null;
                    resolve(result);
                };

                request.onerror = () => {
                    console.error(`❌ IndexedDB get("${key}") error:`, request.error);
                    reject(request.error);
                };
            } catch (error) {
                console.error(`❌ IndexedDB get("${key}") exception:`, error);
                reject(error);
            }
        });
    }

    /**
     * Set a value in IndexedDB
     * @param {string} key
     * @param {any} value
     * @returns {Promise<void>}
     */
    async set(key, value) {
        const db = await this.ensureDB();

        return new Promise((resolve, reject) => {
            try {
                const transaction = db.transaction([this.storeName], 'readwrite');
                const store = transaction.objectStore(this.storeName);

                // Serialize value to handle functions and complex objects
                // IndexedDB can't store functions, so we JSON serialize
                let serializedValue;
                try {
                    serializedValue = JSON.parse(JSON.stringify(value));
                } catch (serializeError) {
                    console.warn(`⚠️ Failed to serialize value for key "${key}", storing as-is:`, serializeError);
                    serializedValue = value;
                }

                const request = store.put(serializedValue, key);

                request.onsuccess = () => {
                    resolve();
                };

                request.onerror = () => {
                    console.error(`❌ IndexedDB set("${key}") error:`, request.error);
                    reject(request.error);
                };
            } catch (error) {
                console.error(`❌ IndexedDB set("${key}") exception:`, error);
                reject(error);
            }
        });
    }

    /**
     * Remove a value from IndexedDB
     * @param {string} key
     * @returns {Promise<void>}
     */
    async remove(key) {
        const db = await this.ensureDB();

        return new Promise((resolve, reject) => {
            const transaction = db.transaction([this.storeName], 'readwrite');
            const store = transaction.objectStore(this.storeName);
            const request = store.delete(key);

            request.onsuccess = () => {
                resolve();
            };

            request.onerror = () => {
                console.error('IndexedDB remove error:', request.error);
                reject(request.error);
            };
        });
    }

    /**
     * Clear all values from IndexedDB
     * @returns {Promise<void>}
     */
    async clear() {
        const db = await this.ensureDB();

        return new Promise((resolve, reject) => {
            const transaction = db.transaction([this.storeName], 'readwrite');
            const store = transaction.objectStore(this.storeName);
            const request = store.clear();

            request.onsuccess = () => {
                resolve();
            };

            request.onerror = () => {
                console.error('IndexedDB clear error:', request.error);
                reject(request.error);
            };
        });
    }

    /**
     * Get all keys from IndexedDB
     * @returns {Promise<string[]>}
     */
    async keys() {
        const db = await this.ensureDB();

        return new Promise((resolve, reject) => {
            const transaction = db.transaction([this.storeName], 'readonly');
            const store = transaction.objectStore(this.storeName);
            const request = store.getAllKeys();

            request.onsuccess = () => {
                resolve(request.result || []);
            };

            request.onerror = () => {
                console.error('IndexedDB keys error:', request.error);
                reject(request.error);
            };
        });
    }

    /**
     * Check if a key exists in IndexedDB
     * @param {string} key
     * @returns {Promise<boolean>}
     */
    async has(key) {
        const value = await this.get(key);
        return value !== null;
    }

    /**
     * Get the number of items in IndexedDB
     * @returns {Promise<number>}
     */
    async length() {
        const db = await this.ensureDB();

        return new Promise((resolve, reject) => {
            const transaction = db.transaction([this.storeName], 'readonly');
            const store = transaction.objectStore(this.storeName);
            const request = store.count();

            request.onsuccess = () => {
                resolve(request.result || 0);
            };

            request.onerror = () => {
                console.error('IndexedDB length error:', request.error);
                reject(request.error);
            };
        });
    }

    /**
     * Close the database connection
     */
    close() {
        if (this.db) {
            this.db.close();
            this.db = null;
            console.log('IndexedDB connection closed');
        }
    }
}

export default IndexedDBStorage;
